import { useEffect } from 'react'
import { z } from 'zod'

import { courseApi } from '../courses/paths'

/** Find Course Feedback's page. */
export const courseFeedbackPath = '/catalog/course-feedback'

/** Find Course Feedback, kept to one course: the address its course filter writes. */
export function courseFeedbackOfPath(courseId: string): string {
  return `${courseFeedbackPath}?${new URLSearchParams({ filter: `${courseFilter}:in:${courseId}` }).toString()}`
}

/** The API's search of course feedback. */
export const courseFeedbackApi = '/catalog/v1/course-feedback'

/** The filter Choose Course sets: the API's course id filter. */
export const courseFilter = 'courseId'

/** Where feedback on a course is given. */
export function giveCourseFeedbackApi(courseId: string): string {
  return `${courseApi(courseId)}/feedback`
}

/** Whether a course takes feedback, and its rating as its owner's settings show it. */
export function courseRatingApi(courseId: string): string {
  return `${courseApi(courseId)}/rating`
}

/** Open the courses published to the viewer, and their ratings. */
export const readCourseCatalogPermission = 'catalog.course-catalog.read'
/** Give feedback on the courses the viewer may see. */
export const writeCourseCatalogPermission = 'catalog.course-catalog.write'
/** See and export everyone's feedback. */
export const readCourseFeedbackPermission = 'catalog.course-feedback.read'

/** Thumbs up or down. */
export const thumbs = ['up', 'down'] as const

export type Thumbs = (typeof thumbs)[number]

/** The longest text the API takes. */
export const textLength = 4000

const feedbackSchema = z.object({
  id: z.string(),
  courseId: z.string(),
  courseName: z.string(),
  thumbs: z.enum(thumbs),
  title: z.string().nullish(),
  text: z.string().nullish(),
  activityId: z.string().nullish(),
  activityName: z.string().nullish(),
  enrollmentId: z.string().nullish(),
  assignmentId: z.string().nullish(),
  userProfileId: z.string(),
  userName: z.string().nullish(),
  createdOn: z.string(),
})

/** One feedback entry, as Find Course Feedback lists it. */
export type CourseFeedback = z.infer<typeof feedbackSchema>

export function parseCourseFeedback(body: unknown): CourseFeedback {
  return feedbackSchema.parse(body)
}

const ratingSchema = z.object({
  courseId: z.string(),
  hasSimpleFeedback: z.boolean(),
  percent: z.number().nullish(),
  count: z.number().nullish(),
})

/** A course's feedback switch, and its rating when shown. */
export type CourseRating = z.infer<typeof ratingSchema>

export function parseCourseRating(body: unknown): CourseRating {
  return ratingSchema.parse(body)
}

const ratingChangedEvent = 'catalog:course-rating-changed'

/** Tells the course's rating on the page that new feedback may have changed it. */
export function announceRatingChanged(courseId: string): void {
  window.dispatchEvent(new CustomEvent<string>(ratingChangedEvent, { detail: courseId }))
}

/** Calls `onChange` whenever feedback is given on the course from this page. */
export function useRatingChanged(courseId: string, onChange: () => void): void {
  useEffect(() => {
    const listener = (event: Event) => {
      if (event instanceof CustomEvent && event.detail === courseId) {
        onChange()
      }
    }
    window.addEventListener(ratingChangedEvent, listener)
    return () => window.removeEventListener(ratingChangedEvent, listener)
  }, [courseId, onChange])
}

/** Whole stars out of five for a rating's percent, as legacy filled them. */
export function starsOf(percent: number): number {
  return Math.min(5, Math.max(0, Math.floor(percent / 20)))
}
